import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SOP_CATEGORIES } from "@/lib/constants";
import { Plus, Save, Trash2, ClipboardList, Loader2 } from "lucide-react";

function SOPEditor({ sop, onSaved }) {
  const [form, setForm] = useState(sop);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(sop), [sop]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/sops/${sop.id}`, { title: form.title, category: form.category, purpose: form.purpose, steps: form.steps, notes: form.notes });
      toast.success("SOP saved.");
      onSaved?.();
    } catch (e) { toast.error("Could not save."); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-4 pt-2">
      <div className="space-y-2">
        <Label>Purpose</Label>
        <Textarea data-testid={`sop-purpose-${sop.id}`} value={form.purpose || ""} onChange={(e) => set("purpose", e.target.value)}
          placeholder="Why does this process exist?" className="rounded-xl min-h-[70px]" />
      </div>
      <div className="space-y-2">
        <Label>Steps</Label>
        <Textarea data-testid={`sop-steps-${sop.id}`} value={form.steps || ""} onChange={(e) => set("steps", e.target.value)}
          placeholder={"1. First, gently…\n2. Then…\n3. Finally…"} className="rounded-xl min-h-[150px]" />
      </div>
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea data-testid={`sop-notes-${sop.id}`} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)}
          placeholder="Anything helpful to remember." className="rounded-xl min-h-[60px]" />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {form.updated_at ? `Last updated ${new Date(form.updated_at).toLocaleDateString()}` : ""}
        </span>
        <Button onClick={save} disabled={saving} data-testid={`sop-save-${sop.id}`} className="rounded-full bg-primary hover:bg-primary/90 gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
        </Button>
      </div>
    </div>
  );
}

export default function SOPLibrary() {
  const [sops, setSops] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newCat, setNewCat] = useState("");
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get("/sops"); setSops(r.data); }
    catch (e) { toast.error("Could not load SOPs."); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!newTitle.trim()) { toast.error("Give your SOP a title."); return; }
    try {
      await api.post("/sops", { title: newTitle.trim(), category: newCat || newTitle.trim() });
      toast.success("SOP added.");
      setAddOpen(false); setNewTitle(""); setNewCat(""); load();
    } catch (e) { toast.error("Could not add."); }
  };

  const remove = async () => {
    try { await api.delete(`/sops/${deleting.id}`); toast.success("SOP deleted."); setDeleting(null); load(); }
    catch (e) { toast.error("Could not delete."); }
  };

  return (
    <div>
      <PageHeader
        testid="sops-header"
        title="SOP Library"
        subtitle="Your step-by-step processes, so nothing has to be remembered from scratch."
        action={
          <Button onClick={() => setAddOpen(true)} data-testid="add-sop-button" className="rounded-full bg-primary hover:bg-primary/90 gap-2">
            <Plus className="h-4 w-4" /> New SOP
          </Button>
        }
      />

      {sops.length === 0 ? (
        <Card className="rounded-3xl border-border shadow-soft p-12 text-center" data-testid="sops-empty">
          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
            <ClipboardList className="h-6 w-6 text-primary" />
          </div>
          <p className="text-muted-foreground">Loading your processes…</p>
        </Card>
      ) : (
        <Card className="rounded-3xl border-border shadow-soft p-2 sm:p-4" data-testid="sops-list">
          <Accordion type="single" collapsible className="w-full">
            {sops.map((s) => (
              <AccordionItem key={s.id} value={s.id} className="border-border px-2" data-testid={`sop-item-${s.id}`}>
                <div className="flex items-center">
                  <AccordionTrigger className="hover:no-underline flex-1" data-testid={`sop-trigger-${s.id}`}>
                    <div className="flex items-center gap-3 text-left">
                      <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <ClipboardList className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-heading text-base">{s.title}</p>
                        {s.purpose && <p className="text-xs text-muted-foreground font-sans mt-0.5 line-clamp-1">{s.purpose}</p>}
                      </div>
                    </div>
                  </AccordionTrigger>
                  <button onClick={() => setDeleting(s)} data-testid={`sop-delete-${s.id}`} className="p-2 rounded-full text-muted-foreground hover:text-destructive shrink-0">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <AccordionContent>
                  <SOPEditor sop={s} onSaved={load} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Card>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-3xl sm:max-w-md" data-testid="sop-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">New SOP</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input data-testid="sop-title-input" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="e.g. Write a blog" className="rounded-xl" autoFocus />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={newCat} onValueChange={setNewCat}>
                <SelectTrigger className="rounded-xl" data-testid="sop-category-select"><SelectValue placeholder="Choose or leave blank" /></SelectTrigger>
                <SelectContent className="rounded-2xl">
                  {SOP_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={create} data-testid="sop-create-button" className="rounded-full bg-primary hover:bg-primary/90">Add SOP</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleting)} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete this SOP?</AlertDialogTitle>
            <AlertDialogDescription>"{deleting?.title}" will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="confirm-delete-sop" className="rounded-full bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
